import React, { RefObject, useImperativeHandle, useRef, useState } from 'react'
import moment from 'moment'
import { createStyles, withStyles } from '@mui/styles'
import { Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { t } from '../../../infrastructure/i18nextHelper'
import { defaultStyles } from '../../../infrastructure/materialUiThemeProvider'
import { Button } from '../../common/customComponents'
import { VesselInfo } from '../dealModels'
import { popupNavigator } from '../../../infrastructure/popupNavigator'
import { Guid } from '../../../infrastructure/guid'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'

export type VesselNavigationDialogArgs = {
    vessels: VesselInfo[]
    productId?: Guid | null
}

function VesselNavigation(props) {
    let settings = UserSettingsContextContainer.useContainer()
    let [openDialog, setOpenDialog] = useState<boolean>(false)
    let [vessels, setVessels] = useState<VesselInfo[]>([])
    let [productId, setProductId] = useState<Guid | null | undefined>(undefined)

    dialogRef = useRef<{ open: (args: VesselNavigationDialogArgs) => void }>(null)

    useImperativeHandle(dialogRef, () => ({
        open: (args: VesselNavigationDialogArgs) => {
            setVessels(args.vessels)
            setProductId(args.productId)
            setOpenDialog(true)
        }
    }))

    let close = () => {
        setVessels([])
        setProductId(undefined)
        setOpenDialog(false)
    }

    let goToVessel = (vesselId: Guid) => {
        close()
        popupNavigator.open('vessel', vesselId, productId ? { productId } : undefined)
    }

    let dateFormat = settings.getDateFormat()
    let shortDateFormat = settings.getShortDateFormat()
    let formatDate = (dateString: string | null) => !!dateString ? moment(dateString).format(dateFormat) : ''
    let formatLaycanRange = (start: string | null, end: string | null) => {
        if (!start && !end) return ''
        let formattedStart = start ? moment(start).format(shortDateFormat) : ''
        let formattedEnd = end ? moment(end).format(shortDateFormat) : ''
        return `${formattedStart} - ${formattedEnd}`
    }

    return (
        <Dialog open={openDialog}
            onClose={close}
            scroll='paper'>
            <DialogTitle>{t('deals.vesselNavigationDialog.title')}</DialogTitle>
            <DialogContent dividers>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('deals.vesselNavigationDialog.vesselName')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.jetty')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.product')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.counterparty')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.originalLaycan')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.availabilityDate')}</TableCell>
                            <TableCell>{t('deals.vesselNavigationDialog.vettingNumber')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vessels.map(vessel => (
                            <TableRow key={vessel.id} hover className={props.classes.clickableRow} onClick={() => goToVessel(vessel.id)}>
                                <TableCell>{vessel.name}</TableCell>
                                <TableCell>{vessel.jetty}</TableCell>
                                <TableCell>{vessel.product}</TableCell>
                                <TableCell>{vessel.counterparty}</TableCell>
                                <TableCell>{formatLaycanRange(vessel.originalLaycanStart, vessel.originalLaycanEnd)}</TableCell>
                                <TableCell>{formatDate(vessel.availabilityDate)}</TableCell>
                                <TableCell>{vessel.vettingNumber}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button className={props.classes.closeButton}
                    label={t('history.close')}
                    onClick={close}
                    color='primary' />
            </DialogActions>
        </Dialog>
    )
}

let styles = () => createStyles({
    clickableRow: { cursor: 'pointer' },
    closeButton: { ...defaultStyles.secondaryButton }
})

let dialogRef: RefObject<{ open: (args: VesselNavigationDialogArgs) => void }> | null = null

export let vesselNavigationDialog = {
    open: (args: VesselNavigationDialogArgs) => {
        dialogRef?.current?.open(args)
    }
}

export let VesselNavigationDialog = withStyles(styles)(VesselNavigation)
